import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { If } from "react-conditional-components-renderer";

import { useDebounce } from "@ascendnow/shared";
import AscSelectInputAndOptionsWrapper from "./AscSelectInputAndOptionsWrapper/AscSelectInputAndOptionsWrapper";
import {
  AscSelectInputOption,
  AscSelectInputOptionProps,
  AscSelectOptionsContainer,
  IAscSelectInputOption,
} from "./AscSelectInputOptions/AscSelectInputOptions";
import { ChevronDownIcon, CloseFilledIcon, CloseIcon } from "../../AscIcons";
import { AscSelectInputProps } from "./AscSelectInput.types";
import { useChildrenArray } from "../../../lib/helpers/hooks/useChildrenArray";
import styles from "./AscSelectInput.module.scss";

function AscSelectInput<T = IAscSelectInputOption["value"]>({
  size = "medium",
  className,
  innerInputClassName,
  inputWrapperClassName,
  selectedDisplayClassName,
  multipleOptionSelectionDisplayClassName,
  optionsTagClassName,
  dropDownClassName,
  defaultValue,
  defaultValueMultiple,
  fullWidth = false,
  showArrowIcon = true,
  arrowIcon,
  onFocus,
  onChange,
  onBlur,
  multiple = false,
  hidden = false,
  children,
  disabled,
  icon,
  optionContainerClassName,
  optionClassName,
  selectedOptionClassName,
  disabledOptionClassName,
  value,
  placeholder,
  isClearSelectedOptions,
  hasClearIcon = true,
  onFetchNextOptions,
  getOptionsOnSearch,
  ...rest
}: AscSelectInputProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>();
  const debouncedSearchValue = useDebounce(inputValue, 300);
  const toggleIconContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<
    IAscSelectInputOption<T>[]
  >([]);
  const options = useChildrenArray<AscSelectInputOptionProps<T>>(
    children || []
  );

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (disabled) {
      return;
    }
    setIsOpen(true);
    onFocus?.(e);
  };
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    if (getOptionsOnSearch && e?.target?.value?.length === 0) {
      getOptionsOnSearch?.();
    }
    setInputValue(e?.target?.value);
  };

  const open = () => {
    if (disabled) {
      return;
    }
    inputRef.current?.focus();
    setIsOpen(true);
  };

  const close = () => {
    inputRef.current?.blur();
    setIsOpen(false);
  };

  const suffixIcon = arrowIcon ? arrowIcon : <ChevronDownIcon />;

  const removeOption = (value: T) => {
    if (disabled) {
      return;
    }
    setSelectedOptions((options) =>
      options?.filter((option) => option.value !== value)
    );
  };

  const clear = () => {
    setInputValue(undefined);
    setSelectedOptions([]);
    if (disabled) {
      return;
    }
  };

  useEffect(() => {
    if (selectedOptions?.length > 0 && inputRef.current) {
      if (inputRef.current.value) {
        inputRef.current.value = "";
      }
    }
  }, [selectedOptions, inputRef]);

  useEffect(() => {
    if (multiple) {
      onChange?.(
        selectedOptions?.map((option) => option?.value) as any,
        selectedOptions as any
      );
      return;
    } else {
      if (selectedOptions.length > 0) {
        onChange?.(selectedOptions[0]?.value as any, selectedOptions[0] as any);
      }
    }
  }, [selectedOptions, multiple]);

  useEffect(() => {
    if (getOptionsOnSearch && debouncedSearchValue) {
      getOptionsOnSearch(debouncedSearchValue);
    }
  }, [debouncedSearchValue, getOptionsOnSearch]);

  useEffect(() => {
    if (isClearSelectedOptions) {
      clear();
    }

    //eslint-disable-next-line
  }, [isClearSelectedOptions]);

  useEffect(() => {
    if (defaultValue && selectedOptions?.length === 0) {
      if (multiple && typeof defaultValue === "string") {
        if (defaultValue.length > 0) {
          const makeOptionsByValue = (defaultValue as string)
            .split(",")
            .map((el) => options.find((option) => option.value === el));
          setSelectedOptions(makeOptionsByValue as IAscSelectInputOption<T>[]);
        }
      } else
        setSelectedOptions([
          {
            value: defaultValue,
            label:
              options?.find((option) => option?.value === defaultValue)
                ?.label || `${defaultValue}`,
          },
        ]);
    }
    if (
      defaultValueMultiple &&
      defaultValueMultiple.length > 0 &&
      selectedOptions?.length === 0
    ) {
      defaultValueMultiple.map((val: any) =>
        setSelectedOptions((prev) => [
          ...prev,
          {
            value: val,
            label:
              options?.find((option) => option?.value === val)?.label ||
              `${val}`,
          },
        ])
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValueMultiple]);

  useEffect(() => {
    if (disabled) {
      return;
    }
    if (multiple) {
      return;
    }
    if (value && options.length > 0) {
      const hasOption = options.find((el) => el?.value === value);
      if (hasOption) {
        setSelectedOptions([hasOption]);
      } else {
        setSelectedOptions([{ value: value as T, label: value as string }]);
      }

      return;
    }
    //eslint-disable-next-line
  }, [value, disabled, options.length > 0]);

  useEffect(() => {
    if (disabled) {
      return;
    }
    if (!multiple) {
      return;
    }
    if (
      Array.isArray(value) &&
      value?.length &&
      value?.sort().join(",") !==
        selectedOptions
          ?.map((option) => option?.value)
          ?.sort()
          .join(",")
    ) {
      setSelectedOptions(
        options
          ?.filter((option) => value.includes(option?.value))
          ?.map((option) => ({
            value: option?.value,
            label: option?.label,
          }))
      );
    }

    //eslint-disable-next-line
  }, [value, disabled, options.length > 0]);

  return (
    <AscSelectInputAndOptionsWrapper
      content={
        <AscSelectOptionsContainer
          options={options}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          focusOnInput={open}
          close={close}
          multiple={multiple}
          searchTerm={inputValue}
          className={optionContainerClassName}
          optionClassName={optionClassName}
          selectedOptionClassName={selectedOptionClassName}
          disabledOptionClassName={disabledOptionClassName}
          onFetchNextOptions={onFetchNextOptions}
        />
      }
      visible={isOpen}
      onClickOutsideContainer={close}
      toggleIconContainerRef={toggleIconContainerRef}
      className={classNames(
        styles.ground,
        {
          [styles.large]: size === "large",
          [styles.medium]: size === "medium",
          [styles.disabled]: disabled,
          [styles.fullWidth]: fullWidth,
          [styles.isOpen]: isOpen,
          [styles.showSingleSelectedOption]:
            inputValue !== "" && selectedOptions?.length > 0 && !multiple,
          [styles.showMultipleSelectedOption]:
            selectedOptions?.length > 0 && multiple,
        },
        className
      )}
      contentClassName={dropDownClassName}
    >
      <div className={classNames(styles.inputWrapper, inputWrapperClassName)}>
        <If test={!!icon}>
          <span className={styles.prefixIcon}>{icon}</span>
        </If>
        {multiple && (
          <div
            className={classNames(
              styles.multipleOptionSelectionDisplay,
              multipleOptionSelectionDisplayClassName
            )}
          >
            {selectedOptions?.map((option) => (
              <span
                className={classNames(styles.optionsTag, optionsTagClassName)}
                key={`${option?.value}`}
              >
                <span>{option?.label}</span>
                <CloseIcon
                  className={styles.removeTagIcon}
                  onClick={() => removeOption(option?.value)}
                />
              </span>
            ))}
          </div>
        )}
        <input
          ref={inputRef}
          className={classNames(styles.innerInput, innerInputClassName)}
          onFocus={handleOnFocus}
          onChange={handleOnChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={selectedOptions?.length > 0 ? "" : placeholder}
          hidden={hidden && selectedOptions?.length > 0}
          {...rest}
          // value=""
          // placeholder={
          //   Array.isArray(rest.value) && rest.value?.length
          //     ? ""
          //     : rest.placeholder
          // }
        />
        <div
          className={classNames(styles.singleOptionSelectionDisplay, {
            [styles.hasPrefixIcon]: !!icon,
          })}
        >
          {selectedOptions[0]?.label}
        </div>
      </div>

      <If test={showArrowIcon && !hasClearIcon}>
        <div ref={toggleIconContainerRef}>
          <span
            className={classNames(styles.suffixIcon, styles.focusIcon)}
            onClick={open}
          >
            {suffixIcon}
          </span>
          <span
            className={classNames(styles.suffixIcon, styles.blurIcon)}
            onClick={close}
          >
            {suffixIcon}
          </span>
        </div>
      </If>
      <If test={!!selectedOptions?.length && hasClearIcon}>
        <span
          className={classNames(styles.suffixIcon)}
          onClick={() => {
            clear();
            onChange?.(undefined, undefined);
          }}
        >
          <CloseFilledIcon />
        </span>
      </If>
    </AscSelectInputAndOptionsWrapper>
  );
}

AscSelectInput.Option = AscSelectInputOption;

export default AscSelectInput;
